/**
 * My Space page
 */
import Dummy from '../../../shared/utils/dummy';
import Rail from '../../../shared/classes/rail';
import Recording from '../../../shared/types/enums/recording';
import TMain from '../templates/main';
import TranslationManager from '../../../shared/assets/locale/translationsManager';
import addonRailRequest from '../../../shared/api/addons/addonRailRequest';
import keepWatchingRequest from '../../../shared/api/initial/keepWatchingRequest';
import recommendationsRequest from '@shared/api/recommendations/request';
import recordingsObservable from '../../../shared/api/recording/recordingsObservable';
import updateRecordings from '../../../shared/actions/recordings/updateRecordings';
import useComplete from '../../../shared/components/utils/hooks/useComplete';
import { IRecordIdentifier } from '../../../shared/types/interfaces/record';
import { Observer, Subscription } from 'rxjs';
import { routerSelector } from '../../../shared/store/slices/router.slice';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../../shared/store/hooks';
import { userSelector } from '../../../shared/store/slices/user.slice';

/**
 * Displays the user's space with recordings, keep watching, recommendations and addons
 */
export default function MySpace() {
  // Selectors
  const { isAnonymous } = useAppSelector(userSelector);
  const {
    params: { current: currentParams },
  } = useAppSelector(routerSelector);

  // State
  const [complete, setComplete] = useComplete();
  const [rails, setRails] = useState<Rail[]>([]);

  // Memoized values
  const dummy = useMemo(() => Dummy.getRecordingRails().data, []);

  // Refs
  const paramsRef = useRef(currentParams);
  const railsRef = useRef(rails);
  paramsRef.current = currentParams;
  railsRef.current = rails;

  // Effect hooks

  useEffect(() => {
    let subscription: Subscription | null;
    // Fetch keep watching data
    if (isAnonymous) {
      setComplete(true);
      return;
    }
    // Fetch data if not complete
    if (!complete) {
      let rails: Rail[] = [];
      Promise.all([
        keepWatchingRequest(),
        recommendationsRequest(),
        addonRailRequest(),
      ]).then((data) => {
        // Destructure rails data
        const keepWatching = data[0];
        const recommendations = data[1];
        const addons = data[2];

        // Initial observer
        const initialObserver: Observer<{
          data: IRecordIdentifier[] | Rail[];
          type: Recording;
        }> = {
          // Next value
          next: (val) => {
            // Finished recordings rails
            if (val.type === Recording.Finished) {
              (val.data as Rail[]).forEach((item: Rail) => {
                item.title =
                  TranslationManager.get('PAGES.MY_SPACE.MY_RECORDINGS') +
                  ' | ' +
                  item.title;
              });
              if (rails.length) {
                rails = (val.data as Rail[]).concat(rails);
              } else {
                rails = rails.concat(val.data as Rail[]);
              }
            }
            // Pending recordings rails
            if (val.type === Recording.Pending) {
              rails = rails.concat(val.data as Rail[]);
            }

            // Update recordings value from store
            updateRecordings(val.data, val.type);
          },
          // Error handling
          error: () => {
            setTimeout(() => {
              setComplete(true);
            }, 500);
          },
          // Complete handling
          complete: () => {
            // Delay so that dummies do not cause a brusque flickering due to fast requests
            setTimeout(() => {
              // Add addons rail on top of the rails (3rd position)
              if (addons) {
                rails.unshift(addons);
              }
              // Add recommendations rail on top of the rails (2nd position)
              if (recommendations) {
                rails.unshift(recommendations);
              }
              // Add keep watching rail on top of the rails (1st position)
              if (keepWatching) {
                rails.unshift(keepWatching);
              }
              // Update rails
              if (rails) {
                setRails(rails as Rail[]);
              }
              // Complete request
              setComplete(true);
            }, 500);
            return null;
          },
        };
        subscription = recordingsObservable().subscribe(
          initialObserver as Partial<Observer<unknown>>
        );
      });
    } else if (!railsRef.current.length && paramsRef?.current?.rails) {
      setRails((paramsRef?.current?.rails as Rail[]) || []);
    }

    return () => {
      subscription?.unsubscribe();
    };
  }, [complete, isAnonymous]);

  // Render
  return (
    <TMain
      complete={complete}
      rails={rails?.length ? rails : dummy}
      sliders={[]}
      startsInMenu={true}
    />
  );
}
